import { Group } from '@visx/group';
import { Pie } from '@visx/shape';
import { useState } from 'react';
import { formatCompactNumber, formatPercent } from '../lib/format.ts';
import { ChartFrame } from './ChartFrame.tsx';
import type { ChartBaseProps } from './ChartFrame.tsx';
import { OTHER_KEY, colourFor } from './palette.ts';

export interface DonutDatum {
  key: string;
  label: string;
  value: number;
}

export interface DonutChartProps extends ChartBaseProps {
  /** 5 parts or fewer; more fold into "Other". */
  data: DonutDatum[];
  format?: (n: number) => string;
  /** The total's caption in the middle: "spent this month". */
  centreLabel?: string;
  order?: string[];
}

const MAX_PARTS = 5;

/**
 * What share of a whole, on a dashboard only (data-viz.md: elsewhere use one stacked bar).
 * 5 parts or fewer, 2 px of surface between them, the total in the middle, and a labelled list
 * beside it with each share, so the shares never depend on colour alone.
 */
export function DonutChart({ data, format = formatCompactNumber, centreLabel, order, height = 160, ...base }: DonutChartProps) {
  const [hover, setHover] = useState<string | null>(null);
  const sorted = [...data].filter((d) => d.value > 0).sort((a, b) => b.value - a.value);
  const parts =
    sorted.length > MAX_PARTS
      ? [...sorted.slice(0, MAX_PARTS - 1), { key: OTHER_KEY, label: 'Other', value: sorted.slice(MAX_PARTS - 1).reduce((n, d) => n + d.value, 0) }]
      : sorted;
  const total = parts.reduce((n, d) => n + d.value, 0);
  const keys = order ?? parts.map((p) => p.key);
  const table = { columns: ['Part', 'Value', 'Share'], rows: parts.map((p) => [p.label, format(p.value), formatPercent(p.value / (total || 1), 1)]) };
  const focus = parts.find((p) => p.key === hover);

  return (
    <ChartFrame {...base} height={height} isEmpty={total <= 0} table={table}>
      {() => {
        const r = height / 2;
        return (
          <div className="flex items-center gap-6">
            <svg width={height} height={height} className="shrink-0">
              <Group top={r} left={r}>
                <Pie<DonutDatum> data={parts} pieValue={(d) => d.value} outerRadius={r} innerRadius={r * 0.68} padAngle={2 / r} pieSort={null}>
                  {(pie) =>
                    pie.arcs.map((arc) => (
                      <path
                        key={arc.data.key}
                        d={pie.path(arc) ?? ''}
                        fill={colourFor(arc.data.key, keys)}
                        opacity={hover && hover !== arc.data.key ? 0.3 : 1}
                        onMouseEnter={() => setHover(arc.data.key)}
                        onMouseLeave={() => setHover(null)}
                        data-part={arc.data.key}
                      />
                    ))
                  }
                </Pie>
                <text textAnchor="middle" y={centreLabel ? -2 : 6} fill="var(--fg)" fontSize={20} fontWeight={600} fontFamily="var(--font-sans)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {format(focus ? focus.value : total)}
                </text>
                {centreLabel && (
                  <text textAnchor="middle" y={16} fill="var(--fg-subtle)" fontSize={12} fontFamily="var(--font-sans)">
                    {focus ? focus.label : centreLabel}
                  </text>
                )}
              </Group>
            </svg>
            <ul className="flex min-w-0 flex-1 flex-col gap-1.5 text-ui">
              {parts.map((p) => (
                <li key={p.key} className="flex items-center gap-2" onMouseEnter={() => setHover(p.key)} onMouseLeave={() => setHover(null)}>
                  <span aria-hidden className="inline-block size-2 shrink-0 rounded-full" style={{ background: colourFor(p.key, keys) }} />
                  <span className="min-w-0 flex-1 truncate text-fg-muted">{p.label}</span>
                  <span className="text-fg num">{format(p.value)}</span>
                  <span className="w-12 text-right text-caption text-fg-subtle num">{formatPercent(p.value / (total || 1))}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      }}
    </ChartFrame>
  );
}
